import { Percentage } from '@sparkdotfi/common-universal'
import { cva } from 'class-variance-authority'

import { AccountSparkPointsSummary, AccountSparkRewardsSummary } from '../../types'
import { savingsTokenToAccountType } from '../common/utils'
import { Actions } from './components/Actions'
import { Details } from './components/Details'
import { Header } from './components/Header'
import { BaseMiniAppOptions } from '@/base-miniapp/types'
import { Token } from '@/domain/types/Token'
import { Panel } from '@/ui/atoms/panel/Panel'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'

export interface DepositCTAPanelProps {
  savingsRate: Percentage
  entryTokens: Token[]
  savingsToken: Token
  description: {
    text: string
    docsLink: string
  }
  apyExplainer: string
  apyExplainerDocsLink: string
  actions: {
    primary: {
      title: 'Connect Wallet' | 'Deposit'
      action: () => void
    }
    secondary: {
      title: 'Try in Sandbox'
      action: () => void
    }
  }
  skipSecondaryAction: boolean
  sparkRewardsSummary: AccountSparkRewardsSummary
  sparkPointsSummary: AccountSparkPointsSummary
  className?: string
  baseMiniAppOptions: BaseMiniAppOptions | undefined
}

export function DepositCTAPanel({
  savingsRate,
  entryTokens,
  savingsToken,
  description,
  apyExplainer,
  apyExplainerDocsLink,
  actions,
  skipSecondaryAction,
  sparkRewardsSummary,
  sparkPointsSummary,
  className,
  baseMiniAppOptions,
}: DepositCTAPanelProps) {
  return (
    <Panel
      spacing="m"
      className={cn(
        'flex flex-col justify-between gap-4',
        panelBgVariants({ bg: savingsTokenToAccountType(savingsToken) }),
        className,
      )}
      data-testid={testIds.savings.account.depositCTA.panel}
    >
      <Header
        savingsRate={savingsRate}
        savingsToken={savingsToken}
        inputTokens={entryTokens}
        apyExplainer={apyExplainer}
        apyExplainerDocsLink={apyExplainerDocsLink}
        sparkRewardsSummary={sparkRewardsSummary}
      />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[3fr_2fr]">
        <Details
          entryTokens={entryTokens}
          savingsToken={savingsToken}
          description={description}
          sparkRewardsSummary={sparkRewardsSummary}
          sparkPointsSummary={sparkPointsSummary}
        />
        <Actions
          actions={actions}
          skipSecondaryAction={skipSecondaryAction}
          className="mt-auto"
          baseMiniAppOptions={baseMiniAppOptions}
        />
      </div>
    </Panel>
  )
}

const panelBgVariants = cva('bg-cover bg-right bg-no-repeat', {
  variants: {
    bg: {
      susds: 'bg-[url(/src/ui/assets/savings/accounts/usds-bg.svg)] bg-primary-inverse',
      susdc: 'bg-[url(/src/ui/assets/savings/accounts/usdc-bg.svg)] bg-primary-inverse',
      sdai: 'bg-[url(/src/ui/assets/savings/accounts/dai-bg.svg)] bg-primary-inverse',
      stusds: 'bg-[url(/src/ui/assets/savings/accounts/stusds-bg.svg)] bg-primary-inverse',
      spusdc: 'bg-[url(/src/ui/assets/savings/accounts/spusdc-bg.svg)] bg-primary-inverse',
      spusdt: 'bg-[url(/src/ui/assets/savings/accounts/spusdt-bg.svg)] bg-primary-inverse',
      speth: 'bg-[url(/src/ui/assets/savings/accounts/speth-bg.svg)] bg-primary-inverse',
      sppyusd: 'bg-[url(/src/ui/assets/savings/accounts/sppyusd-bg.svg)] bg-primary-inverse',
    },
  },
})
